<template>
  <v-card class="pb-5">
    <v-card-title>
      <v-toolbar v-if="$vuetify.breakpoint.mobile" flat class="pb-0 modal-action-toolbar">
        <v-toolbar-title>
          {{ $t('Password') }}
        </v-toolbar-title>

        <v-btn icon @click="$emit('close')">
          <v-icon class="heading grey--text text--darken-4">
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </v-toolbar>

      <span v-else class="text-h5">
        {{ $t('toolbar.changePassword') }}
      </span>
    </v-card-title>

    <v-card-text class="pb-0 px-3">
      <v-container class="pb-0">
        <form-wrapper :validator="$v.formData" :messages="localMessages" :attributes="attributes">
          <form-group name="password">
            <template slot-scope="{attrs}">
              <p class="description-text">
                {{ $t(localMessages.password) }}
              </p>

              <v-text-field
                v-model="formData.oldPassword"
                v-bind="attrs"
                :label="$t('password.currentPassword')"
                :dense="!$vuetify.breakpoint.mobile"
                class="settings-field"
                type="password"
                outlined
                required @input="$v.formData.oldPassword.$touch"
              />
            </template>
          </form-group>

          <form-group name="newPassword">
            <template slot-scope="{attrs}">
              <v-text-field
                v-model="formData.newPassword"
                v-bind="attrs"
                :label="$t('password.new') + ' ' + $t('password.password')"
                :dense="!$vuetify.breakpoint.mobile"
                class="settings-field"
                required
                outlined type="password"
                @input="$v.formData.newPassword.$touch"
              />
            </template>
          </form-group>

          <form-group name="repeatPassword">
            <template slot-scope="{attrs}">
              <v-text-field
                v-model="formData.repeatPassword"
                v-bind="attrs"
                :label="$t('password.repeat') + ' ' + $t('password.password')"
                :dense="!$vuetify.breakpoint.mobile"
                class="settings-field"
                required
                outlined type="password"
                @input="$v.formData.repeatPassword.$touch"
              />
            </template>
          </form-group>
        </form-wrapper>
      </v-container>
    </v-card-text>

    <v-card-actions class="pb-0 pt-2 pr-5">
      <v-spacer />

      <v-btn
        v-if="!$vuetify.breakpoint.mobile"
        color="error"
        text
        @click="$emit('close')"
      >
        {{ $t('ui.cancel') }}
      </v-btn>

      <v-btn
        color="primary"
        dark
        @click="updatePassword(formData)"
      >
        {{ $t('ui.confirm') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>

// libs.
import Auth from '@aws-amplify/auth';
import { templates } from 'vuelidate-error-extractor';
import { required, minLength, sameAs } from 'vuelidate/lib/validators';
import { password } from '@/common-utils/validation';
import { mapActions } from 'vuex';

// components.
import FormGroup from '@/atoms/FormGroup.vue';

const noTrailingWhitespace = (value) => /^[\S]+.*[\S]+$/.test(value);

export default {
  name: 'PasswordForm',
  components: { FormGroup, FormWrapper: templates.FormWrapper },
  validations: {
    formData: {
      oldPassword: { required, password, minLength: minLength(6) },
      newPassword: {
        required, password, noTrailingWhitespace, minLength: minLength(12),
      },
      repeatPassword: {
        required, password, noTrailingWhitespace, minLength: minLength(12), sameAs: sameAs('newPassword'),
      },
    },
  },
  props: {
    dialogOpened: {
      type: Boolean,
    },
    error: { type: String, default: '' },
  },
  data() {
    return {
      formData: {
        oldPassword: '',
        newPassword: '',
        repeatPassword: '',
      },
      attributes: {
        oldPassword: this.$t('password.currentPassword'),
        newPassword: this.$t('password.newPassword'),
        repeatPassword: this.$t('password.repeatPassword'),
      },
      localMessages: {
        required: 'Field {attribute} should be filled out.',
        password: 'Passwords must include a: capital letter, lowercase letter '
          + 'number, and special character',
        sameAs: 'New password and confirm new password fields must match.',
        noTrailingWhitespace: 'Password cannot contain spaces in the beginning or end.',
      },

    };
  },
  computed: {
    userId() {
      return this.$store.getters.user.id;
    },
  },
  methods: {
    ...mapActions(['updateSnackbar']),
    async updatePassword() {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, this.formData.oldPassword, this.formData.newPassword).then(
        (success) => {
          if (success) {
            console.log(`${this.$t('password.updateSuccessful')}`);
            this.$emit('close');
          }
        },
      ).catch((error) => {
        this.updateSnackbar({
          show: true,
          text: `${this.$t('password.updateFailed')}:  ${JSON.stringify(error)}`,
          color: 'error',
          icon: 'mdi-close-circle-outline',
          timeout: 10000,
        });
        console.log(`${this.$t('password.updateFailed')}:  ${JSON.stringify(error)}`);
      });
    },
  },
};

</script>

<style lang="scss" scoped>

@media (max-width: 710px) {
  .v-card {
    display: block;
    overflow: hidden !important;
  }
  .v-card__title {
    padding: 0 8px !important;
  }
  .profile-title {
    font-weight: 500;
    font-size: 17px;
    color: $yester-dark;
  }
  .description-text {
    color: $yester-dark;
    font-weight: 400;
    font-size: 14px;
  }
  .v-card__text {
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow: auto;
    min-height: 20vh;
    max-height: 75vh;
  }
  .v-card__actions {
    margin-right: 0 !important;
    padding-top: 10px !important;
    padding-right: 8px !important;
    padding-left: 10px !important;
    display: block !important;
    height: 15%;
    button {
      width: 100% !important;
      margin-bottom: 10px;
      &.primary-btn {
        margin-left: 0 !important;
      }
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }
}
</style>
